import type { ComponentProps, ReactNode } from 'react';
import type { IntlShape } from 'react-intl';

import {
  DRAFT_APPROVAL_KIND,
  draftResolutionOf,
  isSessionDraftToolName,
} from '../../../contracts/approval-kinds';
import { isClarificationToolName } from '../../../contracts/ask-user-details.types';
import type {
  ApprovalResolution,
  ApprovalVerdict,
  ChatItem,
  ToolUseItem,
} from '../../../contracts/harness.types';
import { hasClinicalTaskApprovalPreview } from '../../../contracts/projects/clinical-task-approval-preview';
import type { ViewedDocumentContext } from '../../../contracts/projects/requests.types';
import { isVerifySubagentToolName } from '../../../contracts/projects/VerifySubagentInlineBlock.utils';
import { isReportIssueToolName } from '../../../contracts/report-issue.types';
import {
  isCreateOrUpdateRoutineTool,
  isEventTriggerRoutineTool,
  isScheduleRoutineTool,
} from '../../../contracts/workflows/lib/routineToolNames';
import { getPendingApprovalIdForItem } from '../../../data/harness/approvals/get-pending-approval-id';
import { isEvidenceSubagentToolName } from '../../../data/projects/components/EvidenceSessionInlineBlock.utils';
import {
  isConnectWaitItem,
  readConnectRequired,
} from '../../../data/projects/integrations/connectRequired';
import { parseProposedPlanInput } from '../../../data/projects/timeline/agent-todo/todoState';
import { CREATE_TODO_PLAN_TOOL_NAME } from '../../../data/projects/timeline/agent-todo/types';
import { deniedApprovalUserNote } from '../../../data/projects/timeline/deniedApprovalUserNote';
import { DraftApprovalBlock } from '../../inline-approval/draft-approval-block';
import type { DraftApprovalBodyRenderArgs } from '../../inline-approval/draft-body';
import {
  isNangoConnectorApprovalTool,
  nangoConnectorApprovalKind,
} from '../../inline-approval/nango-connector-approval';
import { NangoConnectorApprovalCard } from '../../inline-approval/nango-connector-approval-card';
import type { RoutineApprovalCardProps } from '../../inline-approval/routine-approval-card';
import { sessionDraftCard } from '../../inline-approval/session-draft-card';
import type {
  ApprovalRequestBlockProps,
  ApprovalRequestInput,
} from '../components/ApprovalRequestBlock';
import { ApprovalRequestBlock } from '../components/ApprovalRequestBlock';
import { ClinicalTaskApprovalBlock } from '../components/ClinicalTaskApprovalBlock';
import type { ConnectRequiredBlockProps } from '../components/ConnectRequiredBlock';
import { PlanApprovalBlock } from '../components/PlanApprovalBlock';
import type { ReportIssueApprovalTrackProps } from './renderReportIssueApproval';
import { renderReportIssueApproval } from './renderReportIssueApproval';

interface InlineApprovalRendererDeps {
  projectId: string;
  agentRunId: string;
  items?: ChatItem[];
  /** Chat title — plan approval card header fallback. */
  projectTitle?: string;
  onResolveApproval?: (
    approvalId: string,
    verdict: ApprovalVerdict,
    reason?: string,
    modifiedInput?: Record<string, unknown> | null,
    resolution?: ApprovalResolution
  ) => Promise<void>;
  allowChildStreams: boolean;
  intl: IntlShape;
  viewedDocument?: ViewedDocumentContext & { session_id?: string };
  /**
   * Gates the custom Nango HITL cards. When off, Nango write tools still
   * admit inline approval (to avoid a hung shimmer) and fall through to the
   * generic Approve/Cancel card.
   */
  isNangoIntegrationsEnabled?: boolean;
}

/**
 * What the app supplies to the dispatcher. One module-level object at the
 * composition point, so every member keeps one identity.
 */
export interface InlineApprovalRendererHost extends Pick<
  ApprovalRequestBlockProps,
  'desktopControl' | 'useApprovalGuard' | 'renderInputDetails' | 'patientContext'
> {
  /** The draft card's note preview (TipTap). */
  renderDraftBody: (args: DraftApprovalBodyRenderArgs) => ReactNode;
  /** The connect card, bound to the integrations connect buttons. */
  renderConnectRequired: (props: ConnectRequiredBlockProps) => ReactNode;
  /** The routine card the workflows root renders. */
  renderRoutineApproval: (
    props: Pick<RoutineApprovalCardProps, 'approval' | 'onResolve'>
  ) => ReactNode;
  /** The clinical-task card's proposed-tasks row. */
  renderTaskPreview: ComponentProps<
    typeof ClinicalTaskApprovalBlock
  >['renderTaskPreview'];
  /** The quiet receipt a discarded draft leaves. */
  renderTurnStoppedNote: (label: string) => ReactNode;
  /** The Work approval analytics emit. */
  onTrack: (props: ReportIssueApprovalTrackProps) => void;
}

const isDiscardedDraftApproval = (item: ToolUseItem): boolean => {
  const approval = item.approval;
  if (approval?.status !== 'denied') return false;
  const resolution = draftResolutionOf(approval);
  if (resolution) return resolution.kind === 'draft.discarded';
  return (
    approval.approval_kind === DRAFT_APPROVAL_KIND &&
    deniedApprovalUserNote(item) === null
  );
};

const discardedDraftReceipt = (
  item: ToolUseItem,
  intl: IntlShape,
  renderTurnStoppedNote: InlineApprovalRendererHost['renderTurnStoppedNote']
): ReactNode | null => {
  if (!isDiscardedDraftApproval(item)) return null;
  return renderTurnStoppedNote(
    intl.formatMessage({
      defaultMessage: 'Draft discarded',
      id: 'NBrDe2ldvW',
    })
  );
};

export const renderInlineApprovalToolUse = (
  item: ToolUseItem,
  deps: InlineApprovalRendererDeps,
  host: InlineApprovalRendererHost
): ReactNode | null => {
  const discarded = discardedDraftReceipt(item, deps.intl, host.renderTurnStoppedNote);
  if (discarded) return discarded;
  if (isConnectWaitItem(item)) {
    const payload = readConnectRequired(item) ?? {
      kind: 'app',
      connectors: [],
    };
    const pendingApprovalId = getPendingApprovalIdForItem(item);
    return host.renderConnectRequired({
      payload,
      toolCallId: item.id,
      items: deps.items,
      approvalId: pendingApprovalId ?? undefined,
      onResolve: deps.onResolveApproval
        ? (approvalId, verdict) => deps.onResolveApproval!(approvalId, verdict)
        : undefined,
      approvalStatus: item.approval?.status,
    });
  }
  if (!deps.allowChildStreams || !deps.onResolveApproval) return null;
  if (!wouldRenderInlineApproval(item)) return null;

  const draftCard = sessionDraftCard(item, {
    items: deps.items,
    viewedDocument: deps.viewedDocument,
    onResolve: deps.onResolveApproval,
  });
  if (draftCard) {
    return <DraftApprovalBlock {...draftCard.props} renderBody={host.renderDraftBody} />;
  }

  const pendingApprovalId = getPendingApprovalIdForItem(item);
  const toolInput = (item.input as Record<string, unknown> | undefined) ?? {};
  if (!pendingApprovalId) return null;

  const approval: ApprovalRequestInput = {
    approval_id: pendingApprovalId,
    tool_name: item.name,
    tool_input: toolInput,
    tool_call_id: item.id,
    toolCallDisplay: item.toolCallDisplay,
    // Snapshot scopes win over the live-only overlay, mirroring the resolve handler.
    available_override_scopes:
      item.approval?.available_override_scopes ?? item.availableOverrideScopes,
    // Server-authored approval context (`project_tool_approval_context`) — carries
    // the `presentation` the card renders in place of the redacted `tool_input`.
    context: item.approval?.context,
    pendingContext: item.pendingApprovalContext,
  };
  const reportIssue = renderReportIssueApproval({
    item,
    pendingApprovalId,
    toolInput,
    projectId: deps.projectId,
    agentRunId: deps.agentRunId,
    onResolve: deps.onResolveApproval,
    onTrack: host.onTrack,
  });
  if (reportIssue) return reportIssue;

  if (item.approval?.approval_kind === 'clinical_task') {
    // Previewable create-tasks payloads only. Tools tagged clinical_task
    // without tasks[] (e.g. generate_session_clinical_tasks) fall through
    // to the generic card so Approve/Cancel is never lost.
    if (hasClinicalTaskApprovalPreview(approval.tool_input)) {
      return (
        <ClinicalTaskApprovalBlock
          approval={approval}
          onResolve={deps.onResolveApproval}
          renderTaskPreview={host.renderTaskPreview}
        />
      );
    }
  }

  if (
    item.approval?.approval_kind === 'plan' ||
    item.name === CREATE_TODO_PLAN_TOOL_NAME
  ) {
    // Malformed proposal input falls through to the generic approval card so
    // the Approve/Cancel affordance is never lost.
    const proposed = parseProposedPlanInput(item.input);
    if (proposed) {
      return (
        <PlanApprovalBlock
          approval={approval}
          proposed={proposed}
          titleFallback={deps.projectTitle}
          onResolve={deps.onResolveApproval}
        />
      );
    }
  }

  if (
    isCreateOrUpdateRoutineTool(item.name) ||
    isScheduleRoutineTool(item.name) ||
    isEventTriggerRoutineTool(item.name)
  ) {
    return host.renderRoutineApproval({ approval, onResolve: deps.onResolveApproval });
  }

  if (
    deps.isNangoIntegrationsEnabled &&
    nangoConnectorApprovalKind(item.name, approval.tool_input) !== null
  ) {
    return (
      <NangoConnectorApprovalCard
        approval={approval}
        onResolve={deps.onResolveApproval}
      />
    );
  }

  return (
    <ApprovalRequestBlock
      projectId={deps.projectId}
      agentRunId={deps.agentRunId}
      approval={approval}
      onResolve={deps.onResolveApproval}
      isNangoIntegrationsEnabled={deps.isNangoIntegrationsEnabled ?? false}
      desktopControl={host.desktopControl}
      useApprovalGuard={host.useApprovalGuard}
      renderInputDetails={host.renderInputDetails}
      patientContext={host.patientContext}
    />
  );
};

/**
 * True iff the timeline would surface an inline approval card or a discarded
 * draft receipt for this item, instead of leaving it to the generic row or
 * host approval modal.
 *
 * Evidence-adapter tools are *not* excluded here: their rich blocks return
 * null on ``pending_approval`` (see ``makeStatusMapper``), so without the
 * generic ``ApprovalRequestBlock`` a gated write like ``create_template``
 * falls through to a shimmering row with no Approve / Cancel — looking hung.
 * ``clinical_task`` still routes to ``ClinicalTaskApprovalBlock`` below via
 * ``approval_kind``.
 */
export const wouldRenderInlineApproval = (item: ChatItem): item is ToolUseItem => {
  if (item.kind !== 'tool_use') return false;
  if (isDiscardedDraftApproval(item)) return true;
  if (isConnectWaitItem(item)) return true;
  if (item.status !== 'pending_approval') return false;
  if (isClarificationToolName(item.name)) return false;
  if (item.isComputerUse) return true;
  if (isEvidenceSubagentToolName(item.name)) return false;
  if (isVerifySubagentToolName(item.name)) return false;
  if (item.childAgentRunId || item.name === 'invoke_agent') return false;
  if (item.approval?.approval_kind === DRAFT_APPROVAL_KIND) {
    return getPendingApprovalIdForItem(item) !== null;
  }
  // `create_todo_plan`, session-write drafts, and `report_issue` are admitted
  // by name: a missing `approval_kind` render hint must not fall through to a
  // hung shimmer / "Generating document" row while the snapshot is still
  // inflight.
  const isPlanCreate = item.name === CREATE_TODO_PLAN_TOOL_NAME;
  if (
    !item.approval?.approval_kind &&
    !isPlanCreate &&
    !isSessionDraftToolName(item.name) &&
    !isNangoConnectorApprovalTool(item.name) &&
    !isCreateOrUpdateRoutineTool(item.name) &&
    !isScheduleRoutineTool(item.name) &&
    !isEventTriggerRoutineTool(item.name) &&
    !isReportIssueToolName(item.name)
  ) {
    return false;
  }
  return getPendingApprovalIdForItem(item) !== null;
};
